import React, { useEffect, useState } from 'react'
import { Redirect, Route, Switch } from 'react-router-dom'

import { isEmpty } from 'lodash'
import { changeFilterGroups, setDateRange, setFilterGroupsByPage } from 'slices/filters'
import ttiPolyfill from 'tti-polyfill'

import { Box, Flex } from '@driftt/dds'
import { access } from '@driftt/traffic-control'

import { MetricsApi } from 'api'
import AccountDrawer from 'components/AccountDrawer'
import { DateRange, getDefaultRange, getDefaultRangeFor } from 'components/DateRangePicker'
import { driftLoadingStateBuilder } from 'components/States'
import { isBrowser } from 'config/environment'
import { AccountDrawerContext } from 'context/accountDrawerContext'
import { CurrentUserContext } from 'context/currentUserContext'
import { EnabledIntegrationsContext } from 'context/enabledIntegrations'
import { ICPSettingsContext } from 'context/icpSettingsContext'
import { SalesforceContext } from 'context/salesforceContext'
import { ScrollContext } from 'context/scrollContext'
import { SortContext } from 'context/sortContext'
import useICPSettings from 'hooks/useICPSettings'
import { useAppDispatch, useAppSelector } from 'hooks/useRedux'
import useSalesforceAttributeDefs from 'hooks/useSaleforceAttributeDefs'
import { useSessionMetrics } from 'hooks/useSessionMetrics'
import {
  AccountActivitySortValue,
  ActivityEndUser,
  ActivityType,
  ContactType,
  InsightOpportunity
} from 'modules/accountActivity/domain'
import ActivityDashboard from 'pages/ActivityDashboard'
import CCRMAccountDetailsPageV2 from 'pages/CCRMAccountDetailsPage'
import CCRMActivityFeedPage from 'pages/CCRMActivityFeedPage'
import CCRMContactDetailsPageV2 from 'pages/CCRMContactDetailsPage'
import EmailDashboard from 'pages/EmailDashboard'
import MissedAccountsDemoPage from 'pages/MissedAccountsDemoPage'
import ProspectorDemoControlsPage from 'pages/ProspectorDemoControlsPage'
import {
  deserializeDateRange,
  FilterGroupsByPage,
  FilterPage,
  getActivityFiltersFromQueryString
} from 'utils/filters'
import { segmentTrack, TrackType } from 'utils/tracking'

import { Subnav } from './Subnav'

import '../css/index.css'
import styles from './styles.module.css'

interface PersistedData {
  deselectedActivities: ActivityType[]
  deselectedContactTypes: ContactType[]
  sidebarContact: ActivityEndUser | undefined
  accountOwners: { value: number; label: string }[] | undefined

  accountsSort: AccountActivitySortValue
  accountsDescending: boolean
  activitiesSort: AccountActivitySortValue
  activitiesDescending: boolean
  contactsSort: AccountActivitySortValue
  contactsDescending: boolean
  accountActivitySort: AccountActivitySortValue
  accountActivityDescending: boolean
  dealsSort: AccountActivitySortValue
  dealsDescending: boolean
  filterGroupsByPage: FilterGroupsByPage
  activityScrollOffset: number
  accountsScrollOffset: number
  searchQuery: string
  dateRange: DateRange | undefined
  sidebarDeal: InsightOpportunity | undefined
}

const state: PersistedData = {
  deselectedActivities: [],
  deselectedContactTypes: [],
  sidebarContact: undefined,
  accountOwners: [],

  accountsSort: AccountActivitySortValue.TRENDING,
  accountsDescending: true,
  activitiesSort: AccountActivitySortValue.MOST_RECENT,
  activitiesDescending: true,
  contactsSort: AccountActivitySortValue.MOST_RECENT,
  contactsDescending: true,
  accountActivitySort: AccountActivitySortValue.MOST_RECENT,
  accountActivityDescending: true,
  dealsSort: AccountActivitySortValue.DEAL_AMOUNT,
  dealsDescending: true,
  filterGroupsByPage: {
    ACCOUNTS: [[]],
    ACTIVITIES: [[]]
  },
  activityScrollOffset: 0,
  accountsScrollOffset: 0,
  searchQuery: '',
  dateRange: getDefaultRange(),
  sidebarDeal: undefined
}

export const buildRoutes = () => ({
  currentUser,

  enabledIntegrations,
  match: { url }
}: {
  currentUser: any

  enabledIntegrations: any
  match: any
}) => {
  const { dateRange } = useAppSelector(({ filters }) => filters)

  const [accountsSort, setAccountsSort] = useState<AccountActivitySortValue>(state.accountsSort)
  const [accountsDescending, setAccountsDescending] = useState<boolean>(state.accountsDescending)

  const [activitiesSort, setActivitiesSort] = useState<AccountActivitySortValue>(
    state.activitiesSort
  )
  const [activitiesDescending, setActivitiesDescending] = useState<boolean>(
    state.activitiesDescending
  )

  const [contactsSort, setContactsSort] = useState<AccountActivitySortValue>(state.contactsSort)
  const [contactsDescending, setContactsDescending] = useState<boolean>(state.contactsDescending)

  const [accountActivitySort, setAccountActivitySort] = useState<AccountActivitySortValue>(
    state.accountActivitySort
  )
  const [accountActivityDescending, setAccountActivityDescending] = useState<boolean>(
    state.accountActivityDescending
  )

  const [dealsSort, setDealsSort] = useState<AccountActivitySortValue>(state.dealsSort)
  const [dealsDescending, setDealsDescending] = useState<boolean>(state.dealsDescending)

  const [activityScrollOffset, setActivityScrollOffset] = useState(state.activityScrollOffset)
  const [accountsScrollOffset, setAccountsScrollOffset] = useState(state.accountsScrollOffset)

  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [selectedAccountId, setSelectedAccountId] = useState('')

  const {
    accountFitEnabled,
    setAccountFitEnabled,
    accountFitSetup,
    setAccountFitSetup,
    scoringCompletedAtLeastOnce,
    hasFirmoSettings,
    noParameters,
    scoringInProgress,
    companySizes,
    setCompanySizes,
    countries,
    setCountries,
    industries,
    setIndustries,
    ICP_SWR_KEY,
    applyDataToSelects,
    error,
    showPreferencesModal,
    setShowPreferencesModal,
    hideScoresBehindLock,
    defaultData,
    scoringWorkflowError
  } = useICPSettings()

  useEffect(() => {
    return () => {
      state.accountsSort = accountsSort
      state.accountsDescending = accountsDescending
      state.activitiesSort = activitiesSort
      state.activitiesDescending = activitiesDescending
      state.contactsSort = contactsSort
      state.contactsDescending = contactsDescending
      state.accountActivitySort = accountActivitySort
      state.accountActivityDescending = accountActivityDescending
      state.dealsSort = dealsSort
      state.dealsDescending = dealsDescending
    }
  }, [
    currentUser,
    accountsSort,
    accountsDescending,
    activitiesSort,
    activitiesDescending,
    contactsSort,
    contactsDescending,
    accountActivitySort,
    accountActivityDescending,
    dealsSort,
    dealsDescending
  ])

  const { salesforceAttributeDefs } = useSalesforceAttributeDefs()

  useEffect(() => {
    window.localStorage.removeItem(`prospector.dateRange.${currentUser.id}`)
  }, [dateRange, currentUser])

  useEffect(() => {
    if (window.FS?.identify && currentUser?.id && isBrowser) {
      window.FS.identify(currentUser.id, {
        displayName: currentUser.name,
        orgId: currentUser.orgId,
        email: currentUser.email
      })
    }
    // disable this rule because we only want to call this once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (window.location.hash === '#showPrefs') {
      setShowPreferencesModal(true)
    }
  })

  const sessionMetrics = useSessionMetrics()

  const hasEmailDashboardEnabled = access.hasGates('automated_email_dashboard')

  return (
    <SortContext.Provider
      value={{
        accountsSort,
        setAccountsSort,
        accountsDescending,
        setAccountsDescending,

        activitiesSort,
        setActivitiesSort,
        activitiesDescending,
        setActivitiesDescending,

        contactsSort,
        setContactsSort,
        contactsDescending,
        setContactsDescending,

        accountActivitySort,
        setAccountActivitySort,
        accountActivityDescending,
        setAccountActivityDescending,

        dealsSort,
        setDealsSort,
        dealsDescending,
        setDealsDescending
      }}>
      <EnabledIntegrationsContext.Provider value={enabledIntegrations}>
        <CurrentUserContext.Provider value={currentUser}>
          <SalesforceContext.Provider value={salesforceAttributeDefs}>
            <ScrollContext.Provider
              value={{
                accountsScrollOffset,
                setAccountsScrollOffset: offset => {
                  setAccountsScrollOffset(offset)
                  state.accountsScrollOffset = offset
                },
                activityScrollOffset,
                setActivityScrollOffset: offset => {
                  setActivityScrollOffset(offset)
                  state.activityScrollOffset = offset
                }
              }}>
              <ICPSettingsContext.Provider
                value={{
                  accountFitEnabled,
                  setAccountFitEnabled,
                  accountFitSetup,
                  setAccountFitSetup,
                  scoringCompletedAtLeastOnce,
                  hasFirmoSettings,
                  noParameters,
                  scoringInProgress,
                  companySizes,
                  setCompanySizes,
                  countries,
                  setCountries,
                  industries,
                  setIndustries,
                  ICP_SWR_KEY,
                  applyDataToSelects,
                  error,
                  showPreferencesModal,
                  setShowPreferencesModal,
                  hideScoresBehindLock,
                  defaultData,
                  scoringWorkflowError
                }}>
                <AccountDrawerContext.Provider
                  value={{
                    isOpen: isDrawerOpen,
                    setIsOpen: (isOpen: boolean) => {
                      setIsDrawerOpen(isOpen)
                      segmentTrack(isOpen ? TrackType.OPEN_DRAWER : TrackType.CLOSE_DRAWER, {
                        userId: currentUser?.id,
                        orgId: currentUser?.orgId,
                        accountId: selectedAccountId
                      })
                    },
                    accountId: selectedAccountId,
                    setAccountId: setSelectedAccountId
                  }}>
                  <AccountDrawer />
                  <Flex fullHeight gap={0}>
                    <Subnav />
                    <Box
                      css={{
                        position: 'relative',
                        height: '100vh',
                        width: '100%'
                      }}>
                      <Switch>
                        <Route path={url} exact>
                          <ActivityDashboard />
                        </Route>
                        {hasEmailDashboardEnabled && (
                          <Route path={`${url}/emails`} exact>
                            <EmailDashboard />
                          </Route>
                        )}
                        <Route path={`${url}/feed`} exact>
                          <CCRMActivityFeedPage sessionMetrics={sessionMetrics} />
                        </Route>
                        <Route path={`${url}/accounts/:id`} exact>
                          <CCRMAccountDetailsPageV2 />
                        </Route>
                        <Route path={`${url}/contacts/:id`} exact>
                          <CCRMContactDetailsPageV2 />
                        </Route>
                        <Redirect from={`${url}/engagement`} to={url} exact />
                        <Route path={`${url}/demo/missed`} exact>
                          <MissedAccountsDemoPage />
                        </Route>
                        <Route path={`${url}/demo/controls`}>
                          <ProspectorDemoControlsPage />
                        </Route>
                        <Route render={() => <Redirect to={`${url}`} />} />
                      </Switch>
                    </Box>
                  </Flex>
                </AccountDrawerContext.Provider>
              </ICPSettingsContext.Provider>
            </ScrollContext.Provider>
          </SalesforceContext.Provider>
        </CurrentUserContext.Provider>
      </EnabledIntegrationsContext.Provider>
    </SortContext.Provider>
  )
}

const Routes = buildRoutes()

let ttiMetricSent = false

export const ActivitiesPackageSubRoutes = (props: any) => {
  const dispatch = useAppDispatch()
  const { loaded: filtersLoaded } = useAppSelector(({ filters }) => filters)
  const { currentUser } = props

  useEffect(() => {
    if (!ttiMetricSent) {
      ttiPolyfill.getFirstConsistentlyInteractive().then(value => {
        MetricsApi.post('/monitoring/metrics/add', {
          value,
          metric: 'prospector-time-to-interactive',
          dimensions: {
            type: 'bucket',
            buckets: '950,1900,3800,4750,5700'
          }
        })

        ttiMetricSent = true
      })
    }
  }, [])

  useEffect(() => {
    if (currentUser && !filtersLoaded) {
      const qsActivities = getActivityFiltersFromQueryString()
      if (qsActivities) {
        dispatch(changeFilterGroups({ page: FilterPage.ACTIVITIES, filterGroups: qsActivities }))
      } else {
        dispatch(setFilterGroupsByPage(state.filterGroupsByPage))
      }

      const savedDateRangeJson = window.localStorage.getItem(
        `prospector.dateRange.${currentUser.id}`
      )
      if (savedDateRangeJson) {
        const savedDateRange = deserializeDateRange(savedDateRangeJson)
        const parsedDateRange = getDefaultRangeFor(savedDateRange, savedDateRange.type)
        dispatch(setDateRange(parsedDateRange))
      }
    }
  }, [dispatch, currentUser, filtersLoaded])

  if (isEmpty(currentUser?.settings)) {
    const Loading = driftLoadingStateBuilder('Loading...')

    return (
      <div className={styles.loadingContainer}>
        <Loading />
      </div>
    )
  }

  return <Routes {...props} />
}
